import { useState } from 'react';
import DOMPurify from 'dompurify';

const ORDER_URL = process.env.REACT_APP_ORDER_URL;

const Formular = ({ napln }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        telefon: '',
        produkt: '',
        mnozstvo: '',
        prebratie: '',
        adresa: '',
        info: '',
    });
    const [canOrder, setCanOrder] = useState(false);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    // --- Input filters ---
    const handleChange = (e) => {
        const { name, value } = e.target;
        let filtered = value;
        if (name === 'name') {
            filtered = value.replace(/[^a-zA-ZÀ-žáäčďéíľĺňóôŕšťúýžÁÄČĎÉÍĽĹŇÓÔŔŠŤÚÝŽ\s\-]/g, '');
        }
        if (name === 'telefon') {
            filtered = value.replace(/[^\d+\s\-]/g, '').slice(0, 13);
        }
        if (name === 'mnozstvo') {
            filtered = value.replace(/[^\d.,]/g, '').replace(',', '.');
        }
        setFormData((prev) => ({ ...prev, [name]: filtered }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    // --- Validation ---
    const validate = (name, value) => {
        switch (name) {
            case 'name':
                if (!value.trim()) return 'Meno je povinné';
                if (value.trim().length < 2) return 'Meno musí mať aspoň 2 znaky';
                return '';
            case 'email':
                if (!value.trim()) return 'Email je povinný';
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Zadajte platnú emailovú adresu';
                return '';
            case 'telefon':
                if (!value.trim()) return 'Telefón je povinný';
                if (!/^[\d+][\d\s\-]{8,12}$/.test(value)) return 'Zadajte platné telefónne číslo';
                return '';
            case 'produkt':
                return !value ? 'Vyberte produkt' : '';
            case 'mnozstvo':
                if (!value) return 'Množstvo je povinné';
                const n = parseFloat(value);
                if (isNaN(n) || n <= 0) return 'Zadajte kladné číslo';
                if (n > 500) return 'Maximálne množstvo je 500 ton';
                return '';
            case 'prebratie':
                return !value ? 'Vyberte možnosť prebratia' : '';
            case 'adresa':
                if (formData.prebratie === 'dodanie' && !value.trim()) return 'Adresa doručenia je povinná';
                return '';
            default:
                return '';
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setErrors((prev) => ({ ...prev, [name]: validate(name, value) }));
    };

    const inputClass = (field) =>
        `w-full border-b-2 outline-none p-3 bg-transparent transition-colors duration-200 ${
            errors[field]
                ? 'border-red-500 focus:border-red-500'
                : 'border-gray-300 focus:border-red-600'
        }`;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const fieldsToValidate = ['name', 'email', 'telefon', 'produkt', 'mnozstvo', 'prebratie', 'adresa'];
        const newErrors = {};
        fieldsToValidate.forEach((key) => {
            const err = validate(key, formData[key]);
            if (err) newErrors[key] = err;
        });
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setStatus(null);

        const sanitized = {
            name: DOMPurify.sanitize(formData.name),
            email: DOMPurify.sanitize(formData.email),
            telefon: DOMPurify.sanitize(formData.telefon),
            produkt: DOMPurify.sanitize(formData.produkt),
            mnozstvo: DOMPurify.sanitize(formData.mnozstvo),
            prebratie: DOMPurify.sanitize(formData.prebratie),
            adresa: DOMPurify.sanitize(formData.adresa),
            info: DOMPurify.sanitize(formData.info),
        };

        try {
            const res = await fetch(ORDER_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sanitized),
            });

            if (res.ok) {
                setStatus('ok');
                setFormData({ name: '', email: '', telefon: '', produkt: '', mnozstvo: '', prebratie: '', adresa: '', info: '' });
                setCanOrder(false);
            } else {
                setStatus('error');
            }
        } catch {
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-gray-50 py-16 px-6">
            <div className="max-w-2xl mx-auto">
                <h2 className="text-center font-bold text-4xl text-gray-800 mb-2">
                    Objednávka
                </h2>
                <p className="text-center text-gray-500 mb-10 text-sm">
                    Vyplňte formulár a my vás budeme kontaktovať s potvrdením.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Meno */}
                    <div>
                        <input
                            type="text"
                            name="name"
                            placeholder="Meno a priezvisko.."
                            className={inputClass('name')}
                            value={formData.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Email + Telefón */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <input
                                type="email"
                                name="email"
                                placeholder="Váš email.."
                                className={inputClass('email')}
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>
                        <div className="flex-1">
                            <input
                                type="tel"
                                name="telefon"
                                placeholder="Telefónne číslo.."
                                className={inputClass('telefon')}
                                value={formData.telefon}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.telefon && <p className="text-red-500 text-xs mt-1">{errors.telefon}</p>}
                        </div>
                    </div>

                    {/* Produkt */}
                    <div>
                        <select
                            name="produkt"
                            className={inputClass('produkt')}
                            value={formData.produkt}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            <option value="" disabled>Vyberte produkt..</option>
                            {napln.map((option, index) => (
                                <option key={index} value={option}>{option}</option>
                            ))}
                        </select>
                        {errors.produkt && <p className="text-red-500 text-xs mt-1">{errors.produkt}</p>}
                    </div>

                    {/* Množstvo + Prebratie */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <input
                                type="text"
                                name="mnozstvo"
                                placeholder="Množstvo (tony).."
                                className={inputClass('mnozstvo')}
                                value={formData.mnozstvo}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                inputMode="numeric"
                            />
                            {errors.mnozstvo && <p className="text-red-500 text-xs mt-1">{errors.mnozstvo}</p>}
                        </div>
                        <div className="flex-1">
                            <select
                                name="prebratie"
                                className={inputClass('prebratie')}
                                value={formData.prebratie}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            >
                                <option value="" disabled>Spôsob prevzatia..</option>
                                <option value="osobne">Osobne</option>
                                <option value="dodanie">Dodanie</option>
                            </select>
                            {errors.prebratie && <p className="text-red-500 text-xs mt-1">{errors.prebratie}</p>}
                            {formData.prebratie === 'osobne' && (
                                <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-300 text-amber-800 text-xs rounded px-3 py-2">
                                    <span className="mt-0.5">⚠️</span>
                                    <span>Osobný odber je možný až na druhý deň.</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Adresa doručenia — iba pri dodaní */}
                    {formData.prebratie === 'dodanie' && (
                        <div>
                            <input
                                type="text"
                                name="adresa"
                                placeholder="Adresa doručenia.."
                                className={inputClass('adresa')}
                                value={formData.adresa}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                            {errors.adresa && <p className="text-red-500 text-xs mt-1">{errors.adresa}</p>}
                        </div>
                    )}

                    {/* Info */}
                    <div>
                        <input
                            type="text"
                            name="info"
                            placeholder="Dodatočné informácie.. (nepovinné)"
                            className="w-full border-b-2 border-gray-300 outline-none p-3 bg-transparent focus:border-red-600 transition-colors duration-200"
                            value={formData.info}
                            onChange={handleChange}
                        />
                    </div>

                    {/* GDPR */}
                    <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                        <input
                            type="checkbox"
                            className="mt-1 accent-red-600"
                            checked={canOrder}
                            onChange={(e) => setCanOrder(e.target.checked)}
                        />
                        <span>
                            Oboznámil(a) som sa s{' '}
                        <a href="/gdpr" target="_blank" rel="noopener noreferrer" style={{color: '#dc2626'}}>
                            podmienkami ochrany osobných údajov
                        </a>{' '}
                        a súhlasím so spracovaním údajov uvedených vo formulári.
                        </span>
                    </label>

                    {status === 'ok' && (
                        <p className="text-green-600 font-semibold">Objednávka bola úspešne odoslaná!</p>
                    )}
                    {status === 'error' && (
                        <p className="text-red-600 font-semibold">Nastala chyba. Skúste to znova.</p>
                    )}

                    <button
                        type="submit"
                        disabled={!canOrder || loading}
                        className={`w-full py-4 rounded font-semibold text-white transition-colors duration-200 ${
                            canOrder && !loading
                                ? 'bg-red-600 hover:bg-red-700 cursor-pointer'
                                : 'bg-gray-400 cursor-not-allowed'
                        }`}
                    >
                        {loading ? 'Odosielam...' : 'Odošli objednávku'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Formular;
